// @catalog F.030
// KPICardsMargenes — 4 KPIs ejecutivos de /margenes (plan dashboard_executive_redesign).
//   1. Margen bruto ($) + pill %, delta vs LY en pp + sparkline 12m
//   2. Margen % promedio + helper "12m vs Meta 34.0%" + sparkline 12m
//   3. Erosión por descuentos ($) + pill % sobre ventas + helper % del margen + sparkline 12m
//   4. Comisiones vendedores ($) + pill % sobre ventas + helper # vendedores + sparkline 12m
import { useMemo } from "react";
import { Skeleton, Stack } from "@mui/material";

import { KPICard } from "../Dashboard/KPICard";
import MarginsService, {
  type MonthlyMarginsResponse,
  type StoreMarginMetrics,
} from "../../services/margins.service";
import { aggregatePeriodMargins } from "./margenes-format";

interface KPICardsMargenesProps {
  monthlyData: MonthlyMarginsResponse[] | undefined;
  topStores?: StoreMarginMetrics[] | undefined;
  loading: boolean;
  // Meta de margen objetivo configurable (default 34%).
  marginTarget?: number;
}

const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function monthOrder(name: string): number {
  const idx = MONTH_NAMES.indexOf(name.toLowerCase());
  return idx === -1 ? 0 : idx;
}

function avg12m(
  data: MonthlyMarginsResponse[],
  pick: (m: MonthlyMarginsResponse) => number,
): number | null {
  if (data.length === 0) return null;
  const sorted = [...data].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return monthOrder(a.month) - monthOrder(b.month);
  });
  const last12 = sorted.slice(-12);
  if (last12.length === 0) return null;
  const sum = last12.reduce((s, m) => s + pick(m), 0);
  return sum / last12.length;
}

export function KPICardsMargenes(props: KPICardsMargenesProps): JSX.Element {
  const { monthlyData, topStores, loading, marginTarget = 34.0 } = props;

  const computed = useMemo(() => {
    const data = monthlyData ?? [];
    const sortedAsc = [...data].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return monthOrder(a.month) - monthOrder(b.month);
    });
    return {
      sparklineMarginAmount: sortedAsc
        .slice(-12)
        .map((m) => ({ value: m.totalMarginAmount })),
      sparklineMarginPct: sortedAsc
        .slice(-12)
        .map((m) => ({ value: m.avgMarginPercentage })),
      sparklineDiscounts: sortedAsc
        .slice(-12)
        .map((m) => ({ value: m.totalDiscounts })),
      sparklineCommissions: sortedAsc
        .slice(-12)
        .map((m) => ({ value: m.totalCommissions ?? 0 })),
      avg12mPct: avg12m(data, (m) => m.avgMarginPercentage),
    };
  }, [monthlyData]);

  const {
    sparklineMarginAmount,
    sparklineMarginPct,
    sparklineDiscounts,
    sparklineCommissions,
    avg12mPct,
  } = computed;

  if (loading) {
    return (
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={160}
            sx={{ flex: 1, borderRadius: 1 }}
          />
        ))}
      </Stack>
    );
  }

  // KPIs AGREGADOS sobre el período filtrado (no solo el último mes).
  const agg = aggregatePeriodMargins(monthlyData ?? []);
  const marginAmount = agg.marginAmount;
  const marginPct = agg.marginPct;
  const marginPctDeltaPp = agg.marginPctDeltaPp;
  const discounts = agg.discounts;
  const commissions = agg.commissions;
  const revenue = agg.revenue;
  const discountPctOfSales = revenue > 0 ? (discounts / revenue) * 100 : 0;
  const discountPctOfMargin =
    marginAmount > 0 ? (discounts / marginAmount) * 100 : 0;
  const commissionPctOfSales = revenue > 0 ? (commissions / revenue) * 100 : 0;
  const vendorCount = topStores
    ? topStores.filter((s) => s.storeType === "vendor" || s.totalCommissions)
        .length
    : 0;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      sx={{ width: "100%", flexWrap: "wrap" }}
      useFlexGap
    >
      <KPICard
        accent
        title="Margen bruto"
        value={MarginsService.formatCurrency(marginAmount)}
        pill={{ label: `${marginPct.toFixed(1)}%`, tone: "neutral" }}
        deltaPercent={marginPctDeltaPp}
        deltaLabel={
          marginPctDeltaPp !== null
            ? `pp vs. año anterior`
            : "Sin base"
        }
        sparklineData={sparklineMarginAmount}
        tooltip="Margen bruto absoluto del mes ($) — ventas cobradas netas menos costo (definición alineada a cobro bancario). La pill muestra el % sobre ventas; el delta es la diferencia en puntos porcentuales (pp) vs. el mismo mes del año anterior."
      />

      <KPICard
        title="Margen % promedio"
        value={`${marginPct.toFixed(1)}%`}
        helperText={
          avg12mPct !== null
            ? `12m: ${avg12mPct.toFixed(1)}% · Meta: ${marginTarget.toFixed(1)}%`
            : `Meta: ${marginTarget.toFixed(1)}%`
        }
        sparklineData={sparklineMarginPct}
        tooltip="Margen % promedio ponderado del mes vs. promedio 12m y meta del negocio."
      />

      <KPICard
        title="Erosión por descuentos"
        value={MarginsService.formatCurrency(discounts)}
        pill={{
          label: `${discountPctOfSales.toFixed(1)}% s/ventas`,
          tone: "warn",
        }}
        helperText={
          marginAmount > 0
            ? `${discountPctOfMargin.toFixed(0)}% del margen`
            : undefined
        }
        sparklineData={sparklineDiscounts}
        tooltip="Total de descuentos aplicados en el mes — directos + por lista de precios. El pill muestra el % sobre ventas brutas; el helper muestra cuánto representa del margen bruto."
      />

      <KPICard
        title="Comisiones vendedores"
        value={MarginsService.formatCurrency(commissions)}
        pill={{
          label: `${commissionPctOfSales.toFixed(1)}% s/ventas`,
          tone: "neutral",
        }}
        helperText={
          vendorCount > 0 ? `${vendorCount} vendedores` : "Solo canal POS"
        }
        sparklineData={sparklineCommissions}
        tooltip="Comisiones acumuladas a vendedores SO (Sale Orders) en el mes."
      />
    </Stack>
  );
}
